import { useState } from 'react'
import { api } from '../api/client.ts'
import type { Poll } from '../api/types.ts'

/**
 * ОПРОС В ПЕРЕПИСКЕ
 *
 * До того как человек ответил, чужие результаты не показываются: увидев,
 * что все выбрали первое, половина проголосует так же не думая. После
 * своего голоса видно всё.
 *
 * Исключение — завершённый опрос: там результаты видны сразу, голосовать
 * уже всё равно нельзя.
 */
export function PollCard({ poll, own }: { poll: Poll; own: boolean }) {
  const [state, setState] = useState(poll)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const voted = state.myVote.length > 0
  const showResults = voted || state.closed

  const vote = async (optionId: string) => {
    if (state.closed || busy) return

    // При выборе нескольких вариантов нажатие добавляет или снимает,
    // при одном — заменяет
    const next = state.multiple
      ? state.myVote.includes(optionId)
        ? state.myVote.filter((id) => id !== optionId)
        : [...state.myVote, optionId]
      : [optionId]

    setBusy(true)
    setError(null)
    try {
      const data = await api<{ poll: Poll }>(`/api/polls/${state.id}/vote`, {
        method: 'POST',
        body: { optionIds: next },
      })
      setState(data.poll)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const close = async () => {
    setBusy(true)
    try {
      const data = await api<{ poll: Poll }>(`/api/polls/${state.id}/close`, { method: 'POST' })
      setState(data.poll)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={'poll' + (own ? ' is-own' : '')}>
      <div className="poll-question">{state.question}</div>
      <div className="poll-note">
        {state.closed
          ? 'Опрос завершён'
          : state.anonymous
            ? 'Тайное голосование'
            : state.multiple
              ? 'Можно выбрать несколько'
              : 'Выбери один вариант'}
      </div>

      <div className="poll-options">
        {state.options.map((option) => {
          const mine = state.myVote.includes(option.id)
          return (
            <button
              key={option.id}
              className={'poll-option' + (mine ? ' is-mine' : '') + (showResults ? ' is-result' : '')}
              onClick={() => void vote(option.id)}
              disabled={busy || state.closed}
            >
              {showResults && (
                <span className="poll-fill" style={{ width: `${option.percent}%` }} />
              )}

              <span className="poll-mark" aria-hidden="true">
                {mine ? '●' : '○'}
              </span>
              <span className="poll-text">{option.text}</span>

              {showResults && (
                <span className="poll-count">
                  {option.percent}%
                  {!state.anonymous && option.voters.length > 0 && (
                    <span className="poll-voters">
                      {option.voters.map((v) => v.displayName.split(' ')[0]).join(', ')}
                    </span>
                  )}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="poll-foot">
        <span>
          {state.totalVoters === 0
            ? 'Пока никто не ответил'
            : `Ответили: ${state.totalVoters}`}
        </span>
        {own && !state.closed && (
          <button className="poll-close" onClick={() => void close()} disabled={busy}>
            Завершить
          </button>
        )}
      </div>

      {error && <div className="poll-error">{error}</div>}
    </div>
  )
}
